import type { AnalyticsInterface } from '@jitsu/protocols/analytics'
import { store } from '@chilipiper/utils'
import {
  environment,
  type ChiliTrackingInterface,
  type DebugMode,
  type Product,
  type ProductEvent,
  type TrackingEventCallback,
  type TrackingEventName,
  type TrackingInitParams,
  type TrackingTraits,
} from '@chilipiper/web-data-external'
import { CHILIPIPER_DEANON_AND_TRACKING_INTEGRATION_EVENT } from '../shared/constants'
import { loadEnvConfig } from '../shared/env'
import { isLiftAiItem, normalizeDeanonymizationItem } from '../shared/normalizeDeanonymization'
import type { DeanonTrackingEvent } from '../shared/types'
import { initJitsu, startAnalyticsListeners, stopAnalyticsListeners } from './analyticsSetup'
import { jitsu, setTrackingMode } from './jitsuWrapper'
import { CHILI_TRACKING_DEBUG_KEY, logger } from './logger'
import { offTrackingEvent, onTrackingEvent } from './trackingEvents'
import { sanitizeTraits } from './traits'
import {
  initConsentHandling,
  registerAnalyticsControls,
  enableTracking,
  disableTracking,
} from './consentHandling'

class ChiliTracking implements ChiliTrackingInterface {
  #isInitialized = false
  #guestId: string | null = null
  #deanonHandler: ((event: MessageEvent) => void) | null = null

  async init({ tenantId, mode = 'full' }: TrackingInitParams): Promise<void> {
    if (this.#isInitialized) {
      logger.warn(
        'ChiliTracking.init called twice — ignoring. Reload the page to re-initialize with different configuration.'
      )
      return
    }
    this.#isInitialized = true

    // Set before anything can queue an event: the wrapper decides on the way out what the mode
    // lets through, whether the call came from a listener or from an explicit API call.
    setTrackingMode(mode)

    // Subscribe to deanon postMessage events early — before any async work —
    // so we don't miss events fired by deanon while env-config or jitsu are loading.
    this.#subscribeToDeanonEvents()

    const envLoaded = await loadEnvConfig(`${environment.baseUrl}/web-data/env-config.js`, e =>
      logger.error('Error loading env file:', e)
    )
    if (!envLoaded) return

    if (!tenantId) {
      logger.error('tenantId is required')
      return
    }

    logger.log('Initializing ChiliTracking')

    initConsentHandling()

    const jitsuReady = await initJitsu({ tenantId })
    if (jitsuReady) {
      // Re-identify after consent accept or tracking re-enable — Jitsu loses identity traits
      // when listeners are stopped, so we need to re-send the guestId that deanon originally
      // provided via postMessage.
      const reIdentifyGuest = () => {
        if (this.#guestId) {
          jitsu.identify(null, { guestId: this.#guestId })
        }
      }

      if (mode !== 'full') {
        // The wrapper already drops whatever these would produce, but the listeners are not
        // started either: the route-change handler registers once behind a module-level latch
        // that stopAnalyticsListeners cannot undo, so registering it would leave a permanent
        // hook on history for events that can never be sent.
        registerAnalyticsControls(reIdentifyGuest, () => {})
      } else {
        registerAnalyticsControls(() => {
          startAnalyticsListeners()
          reIdentifyGuest()
        }, stopAnalyticsListeners)
      }
    }

    logger.log(`ChiliTracking initialized (${mode} mode)`)
  }

  productImpression(product: string): void {
    jitsu.track('ProductImpression', { product })
  }

  productEvent<P extends Product>(product: P, event: ProductEvent<P>): void {
    jitsu.track(event.type, { product, data: JSON.stringify(event.data) })
  }

  page(): void {
    jitsu.page()
  }

  identify(traits: TrackingTraits): void {
    const sanitized = sanitizeTraits(traits)
    if (!sanitized) return
    jitsu.identify(null, sanitized)
  }

  on(event: TrackingEventName | '*', callback: TrackingEventCallback): () => void {
    return onTrackingEvent(event, callback)
  }

  off(event: TrackingEventName | '*', callback: TrackingEventCallback): void {
    offTrackingEvent(event, callback)
  }

  enableTracking(): void {
    enableTracking()
  }

  disableTracking(): void {
    disableTracking()
  }

  setDebug(mode: DebugMode): void {
    store.set(CHILI_TRACKING_DEBUG_KEY, mode)
    jitsu.configure({ debug: mode === 'verbose' })
  }

  #subscribeToDeanonEvents(): void {
    this.#deanonHandler = (event: MessageEvent) => {
      const eventInfo = event.data
      if (eventInfo?.type !== CHILIPIPER_DEANON_AND_TRACKING_INTEGRATION_EVENT) return

      let data: DeanonTrackingEvent
      try {
        data = JSON.parse(eventInfo.data) as DeanonTrackingEvent
      } catch {
        return
      }

      if (data.type === 'Identify') {
        this.#guestId = data.guestId
        jitsu.identify(null, { guestId: data.guestId })
      }

      if (data.type === 'Deanonymization') {
        // LiftAi is intent data, not company deanonymization — it's intentionally
        // skipped on the jitsu pipeline per CDP-135 / CDP-149 scope. The
        // web-data deanon BE consumes LiftAi; jitsu doesn't yet have an event
        // schema for it.
        const normalizedData = data.data
          .filter(item => !isLiftAiItem(item))
          .map(item => normalizeDeanonymizationItem(item))
        jitsu.track('Deanonymization', normalizedData)
      }
    }
    window.addEventListener('message', this.#deanonHandler)
  }
}

logger.info(`v${__APP_VERSION__} loaded`)

declare global {
  interface Window {
    jitsu?: AnalyticsInterface
    __cpJitsuOnInit?: () => void
    jitsuConfig?: Record<string, unknown>
  }
}

window.ChiliTracking = new ChiliTracking()
